import React from "react";
import { Gift, Sparkles } from "lucide-react";
import VoucherDialog from "@/components/site/VoucherDialog";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";

const WOMAN_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/happy_customer.jpg`;

export default function VoucherBanner() {
  const { t } = useT();
  return (
    <section
      data-testid="voucher-banner"
      id="voucher"
      className="relative py-16 lg:py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 overflow-hidden"
    >
      <div className="absolute -top-12 -right-12 h-64 w-64 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 h-72 w-72 rounded-full bg-rose-200/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
            <Sparkles className="h-3 w-3" /> {t("voucher.tag")}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-navy leading-[1.05]">
            {t("voucher.title1")}{" "}
            <span className="text-amber-600">{t("voucher.title2")}</span>{" "}
            {t("voucher.title3")}
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-xl text-base sm:text-lg">
            {t("voucher.body")}
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <VoucherDialog
              testIdPrefix="banner-voucher"
              trigger={
                <Button
                  data-testid="banner-voucher-cta"
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white h-12 px-7 text-base font-semibold gap-2 shadow-[0_14px_30px_-12px_rgba(245,158,11,0.6)]"
                >
                  <Gift className="h-4 w-4" /> {t("cta.claimTenVoucher")}
                </Button>
              }
            />
            <Button
              asChild
              variant="outline"
              className="rounded-full h-12 px-7 text-base font-semibold border-amber-300 bg-white hover:bg-amber-50 text-brand-navy"
            >
              <a href="#how-it-works">{t("cta.howItWorks")}</a>
            </Button>
          </div>

          <div className="mt-7 flex items-center gap-5 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t("voucher.instantCode")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t("voucher.noCard")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t("voucher.firstTime")}
            </span>
          </div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <div className="absolute -inset-4 bg-white/60 rounded-[2rem] blur-2xl -z-10" />
          <div className="relative rounded-[2rem] overflow-hidden border border-white shadow-2xl bg-white">
            <img
              src={WOMAN_IMG}
              alt="A happy customer holding freshly cleaned laundry"
              className="w-full h-[360px] sm:h-[420px] object-cover" loading="lazy" decoding="async" />
            <div
              data-testid="voucher-floating-tag"
              className="absolute top-5 right-5 rounded-2xl bg-white shadow-xl px-4 py-3 border border-slate-100"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t("voucher.welcomeGift")}
              </p>
              <p className="mt-1 font-display text-3xl font-bold text-amber-600 leading-none">
                $10
              </p>
              <p className="mt-1 text-[11px] font-semibold text-slate-600">
                {t("voucher.offFirst")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
